export const projects = [
  {
    id: 'swetrix',
    title: "Swetrix Analytics",
    summary: "Open-source, GDPR-compliant web analytics platform built as a privacy-first alternative to Google Analytics. Handles 1M+ requests/sec with real-time dashboards, AI-powered traffic forecasting, and zero personal data collection.",
    metrics: [
      { value: "1M+", label: "req/sec" },
      { value: "1B+", label: "events processed" },
      { value: "Open Source", label: "fully self-hostable" },
    ],
    overview: {
      description: `Swetrix Analytics is a GDPR-compliant, open-source web analytics platform focused on privacy, transparency, and performance. Built as a modern alternative to Google Analytics, it offers real-time traffic insights without compromising user data — no cookies, no fingerprinting, and no personal data collection.

The platform handles over 1 million requests per second using an optimized ClickHouse backend, offering sub-second latency for real-time dashboards. The frontend is built in React, while the API and service logic are powered by a NestJS-based backend with fine-grained telemetry.

Swetrix is designed with developer control in mind: customizable dashboards, webhook-based alerts, and built-in support for external integrations like Slack and Discord. Teams can monitor traffic trends, define custom events, and track uptime for both frontend and backend services.

An AI-powered forecasting module (Swetrix-AI-Prophet) predicts traffic patterns using time-series analysis, helping teams plan for scale and understand user behavior. Uptime monitoring, alerting, and infrastructure health metrics are also built in—making Swetrix not just a web analytics tool, but a lightweight observability suite for web projects.

It's actively developed and self-hostable, making it ideal for teams who want full ownership of their analytics stack with no vendor lock-in.`,
      role: "Co-founder & CTO",
      problem: "Privacy concerns with traditional analytics and need for transparent, open-source alternatives"
    },
    technologies: [
      "Python", "JavaScript",
      "React", "NestJS",
      "MySQL", "ClickHouse", "Redis", "Elasticsearch",
      "Docker", "Kubernetes", "Terraform", "Prometheus", "Grafana"
    ],
    timeline: {
      duration: "4+ years (2021 - Present)",
      status: "Active"
    },
    features: [
      { title: "Real-time Analytics", description: "Live dashboard with sub-second data updates" },
      { title: "Privacy Compliance", description: "GDPR-compliant with no personal data collection and transparent data processing" },
      { title: "High Performance", description: "Optimized ClickHouse queries handling 1M+ requests/second" },
      { title: "AI Predictions", description: "Swetrix-AI-Prophet service for traffic forecasting using time series analysis" },
      { title: "Uptime Monitoring", description: "Uptime monitoring for all services and infrastructure" },
      { title: "Customisable Dashboards", description: "Customisable dashboards for different teams and use cases" },
      { title: "Alerts and Notifications", description: "Alerts and notifications for different events and metrics" },
      { title: "External Integrations", description: "Integrations with Slack, Discord, and more" },
    ],
    challenges: [
      {
        title: "Real-time Data Processing",
        description: "Achieving sub-second latency while processing millions of events",
        solution: "Implemented custom Redis pub/sub architecture with optimized ClickHouse ingestion pipelines"
      },
      {
        title: "Scalability at Low Cost",
        description: "Maintaining performance while keeping infrastructure costs minimal",
        solution: "Designed efficient data compression and query optimization strategies"
      }
    ],
    screenshots: [
      { url: "/projects/swetrix/dashboard.webp", alt: "Swetrix Analytics Dashboard" },
      { url: "/projects/swetrix/custom_events.webp", alt: "Custom Events Tracking" },
      { url: "/projects/swetrix/performance_monitoring.webp", alt: "Performance Monitoring" }
    ],
    links: {
      github: "https://github.com/swetrix/swetrix",
      demo: "https://swetrix.com"
    },
    documentation: {
      description: "Comprehensive architecture documentation including deployment guides and API references.",
      links: [
        { title: "Architecture Docs", url: "https://docs.swetrix.com" },
        { title: "API Reference", url: "https://api.swetrix.com" }
      ]
    },
    reflection: {
      learned: "Building a privacy-first product requires careful consideration of every data touchpoint. Performance optimization is crucial for real-time systems. I learned a lot about the importance of privacy and the need for a privacy-first approach to data collection and processing.",
      differently: "I would have focused more on the frontend and user experience from the beginning. I would have also focused more on the backend and performance optimization from the beginning."
    }
  },
  {
    id: 'lifegraph',
    title: "LifeGraph",
    summary: "AI-powered goal intelligence platform that decomposes life aspirations into structured roadmaps with milestones, tasks, and daily habits. Features interactive 3D goal visualization, personalized AI coaching, and a gamification engine.",
    metrics: [
      { value: "150+", label: "API Endpoints" },
      { value: "AI", label: "coaching engine" },
      { value: "3D", label: "goal visualization" },
    ],
    overview: {
      description: `LifeGraph is an AI-powered goal intelligence platform that transforms vague life aspirations into structured, actionable roadmaps. It uses advanced AI to decompose high-level goals into concrete milestones, tasks, and daily habits — then tracks progress through an intelligent coaching system.

The platform features a multi-view goal management interface (Kanban, Timeline, Table, and Graph views) powered by D3.js and Three.js for interactive goal visualization. A Neo4j graph database models the relationships between goals, milestones, and tasks, enabling AI-driven insights about dependencies and optimal sequencing.

The AI coaching system provides personalized guidance through natural language chat, adapting recommendations based on user progress patterns, personality traits, and life area balance. A gamification engine with XP, levels, streaks, and achievements keeps users motivated and engaged.

Built with a modern stack: FastAPI backend with PostgreSQL and Neo4j, Next.js 16 frontend with TypeScript and Tailwind CSS, and a full Kubernetes infrastructure with observability (Prometheus, Grafana, OpenTelemetry, Sentry).`,
      role: "Founder & Lead Developer",
      problem: "People struggle to turn big life aspirations into actionable plans, losing motivation without structure, tracking, and intelligent guidance."
    },
    technologies: [
      "Python", "TypeScript",
      "FastAPI", "Next.js 16", "React 19",
      "PostgreSQL", "Neo4j", "Redis",
      "OpenAI", "Claude", "Celery",
      "D3.js", "Three.js", "Tailwind CSS 4",
      "Stripe", "SendGrid", "S3",
      "Docker", "Kubernetes", "Prometheus", "Grafana", "Sentry"
    ],
    timeline: {
      duration: "Ongoing (2025 - Present)",
      status: "Live"
    },
    features: [
      { title: "AI Feasibility Validation", description: "Scores goals 0-100 for feasibility given your current commitments, skills, and timeline — with actionable suggestions when scores are low" },
      { title: "Graph-Based Task Decomposition", description: "Neo4j-powered dependency-aware task generation — not flat lists, but directed acyclic graphs with prerequisites, time estimates, and cross-goal conflict detection" },
      { title: "Gamification Engine", description: "XP system, 50 levels, 4-tier achievements (Common to Legendary), streaks, daily check-ins, and 100+ skill trees with mastery tracking" },
      { title: "Template Marketplace", description: "Community-driven goal templates — create from completed goals, share, review, and one-click import with full task graph" },
      { title: "Progress Journal", description: "Time-tracked progress entries with image/document attachments, and memory wall gallery" },
      { title: "Psychological Profiling", description: "AI-generated Big 5 personality, SDT motivation, cognitive style, and risk profile analysis — coaching adapts to your psychology" },
    ],
    challenges: [
      {
        title: "Dual-Database Architecture at Scale",
        description: "Modelling goals as both relational data (users, subscriptions, analytics) and graph data (task dependencies, cross-goal relationships) required two databases working in sync",
        solution: "PostgreSQL for structured data (30+ models via SQLAlchemy 2.0) and Neo4j for relationship traversal. Celery workers keep both databases consistent asynchronously, with Redis caching hot paths"
      },
      {
        title: "AI Coaching That's Actually Useful",
        description: "Generic AI advice ('set SMART goals!') is the #1 reason AI goal tools fail. The coach needed to be genuinely contextual and specific",
        solution: "Built a context pipeline that injects the user's full goal graph, completion velocity, streak data, skill progress, and psychological profile into every AI interaction. Intent detection routes to specialised response templates (progress check, motivation, focus, patterns)"
      },
      {
        title: "Solo-Building a Production SaaS",
        description: "Shipping 150+ endpoints, 30+ models, 3D visualisation, Stripe billing, email, storage, gamification, and Kubernetes deployment as a single developer",
        solution: "Ruthless prioritisation, AI-assisted development (Claude Code for velocity), modular service architecture, and comprehensive testing (2,100+ tests) to move fast without breaking things"
      }
    ],
    screenshots: [
      { url: "/projects/lifegraph/main_page.webp", alt: "LifeGraph Landing Page" },
      { url: "/projects/lifegraph/goal_graph.webp", alt: "Dashboard with XP, Goals & Daily Check-in" },
      { url: "/projects/lifegraph/gamification.webp", alt: "Gamification" },
      { url: "/projects/lifegraph/marketplace.webp", alt: "Templates Marketplace" },
      { url: "/projects/lifegraph/universe.webp", alt: "Universe" },
      { url: "/projects/lifegraph/dashboard.webp", alt: "Dashboard" },
      { url: "/projects/lifegraph/progress.webp", alt: "Progress" },
      { url: "/projects/lifegraph/profile.webp", alt: "Profile" },

    ],
    links: {
      demo: "https://lifegraph.tech"
    },
    reflection: {
      learned: "Building an AI-first product taught me that prompt engineering is a core engineering discipline, not an afterthought. The dual-database architecture showed me how choosing the right data model per domain (graphs for relationships, relational for everything else) eliminates entire categories of complexity. Most importantly: ship fast, learn from users, iterate — I over-built before validating, and that's the one thing I'd change.",
      differently: "Launch earlier with fewer features. The AI coaching + feasibility validation is the core — I should have shipped that alone in 4 weeks, got 100 users, and built the gamification and 3D universe only after confirming retention. Feature count doesn't equal product-market fit."
    }
  },
  {
    id: 'gipant',
    title: "GiPant",
    summary: "Donation platform connecting Norwegian consumers with local sports clubs through glass bottle recycling. Multi-user system with geolocation-based pickup matching, real-time status tracking, and PowerOffice accounting integration.",
    metrics: [
      { value: "4", label: "user roles" },
      { value: "5-10km", label: "geo-matching" },
      { value: "18 mo", label: "build time" },
    ],
    overview: {
      description: `GiPant is a comprehensive donation platform that connects end consumers with Norwegian sports clubs through glass bottle recycling. The system enables users to donate bottles to their favorite sports clubs, teams, and players while earning rewards and supporting local sports communities.

The platform features a sophisticated multi-user architecture supporting end consumers, sports clubs, teams, and players/parents, each with distinct functionality and donation management capabilities.`,
      role: "Lead Backend Developer",
      problem: "Local sports clubs in Norway struggle with funding, while consumers want convenient ways to support their favorite teams through sustainable practices."
    },
    technologies: [
      "Python", "Go", "JavaScript",
      "Django", "React", "GraphQL",
      "PostgreSQL", "Redis",
      "OpenCV", "Celery",
      "Kubernetes", "Terraform", "AWS", "Prometheus", "Grafana"
    ],
    timeline: {
      duration: "18 months (2023-2025)",
      status: "Complete"
    },
    features: [
      { title: "Multi-User Platform", description: "Comprehensive user management system supporting end consumers, sports clubs, teams, and players with role-based access and functionality." },
      { title: "Donation Management", description: "E2E donation workflow from pickup requests to completion, including status tracking, receipt management, and financial reporting." },
      { title: "Geolocation Services", description: "Location-based pickup request matching within 5-10km radius, enabling efficient donation coordination between consumers and sports organizations." },
      { title: "Rewards & Analytics", description: "Comprehensive reward system with donation statistics, progress tracking, and detailed analytics for clubs, teams, and individual players." },
      { title: "Admin Dashboard", description: "Advanced GraphQL admin interface for sports clubs to manage teams, players, donations, and generate detailed financial reports." },
      { title: "Invitation System", description: "Secure invitation-based registration system with separate links for players and parents, ensuring proper user type classification." },
      { title: "Subscription System", description: "Invoices for the sport clubs to pay % for the donations." },
      { title: "PowerOffice Integration", description: "Integration with PowerOffice (Norwegian accounting software) for the sport clubs to manage their invoices and payments." }
    ],
    challenges: [
      {
        title: "Complex User Hierarchy",
        description: "Managing the intricate relationships between clubs, teams, players, and parents while maintaining data integrity and proper access controls.",
        solution: "Implemented a robust role-based access control system with clear user hierarchies and permission management through Django's authentication framework."
      },
      {
        title: "Geolocation Matching",
        description: "Efficiently matching pickup requests with available players within specific radius while considering real-time availability and preferences.",
        solution: "Developed a location-based matching algorithm using PostgreSQL's PostGIS extension and Redis for caching frequently accessed location data."
      },
      {
        title: "Real-time Status Updates",
        description: "Ensuring all stakeholders receive timely updates about donation status changes and pickup request modifications.",
        solution: "Implemented WebSocket connections and Redis pub/sub for real-time notifications, with Celery handling background task processing."
      }
    ],
    screenshots: [
      { url: "/projects/gipant/team_app.webp", alt: "Team App" },
      { url: "/projects/gipant/player_app.webp", alt: "Player App" },
      { url: "/projects/gipant/admin_interface.webp", alt: "Admin Interface" }
    ],
    links: {
      demo: "https://gipant.no/en/henter-pant"
    },
    reflection: {
      learned: "Building a platform that serves multiple user types requires careful consideration of user experience, data relationships, and real-time communication. The geolocation features taught me valuable lessons about optimizing location-based services and managing complex spatial queries.",
      differently: "I would implement more comprehensive caching strategies from the start and consider using a dedicated message queue system earlier in the development process to handle the high volume of real-time updates."
    }
  },
  {
    id: 'cityvoice',
    title: "City Voice",
    summary: "Smart city platform enabling real-time citizen-government communication for municipal service management. Currently being sold to the British Council across the UK for managing service requests.",
    metrics: [
      { value: "UK-wide", label: "council rollout" },
      { value: "Real-time", label: "issue tracking" },
      { value: "iOS+Android", label: "mobile apps" },
    ],
    overview: {
      description: `City Voice is a smart city platform that facilitates real-time communication between citizens and local governments. Designed for municipal service management, it allows residents to report issues, submit requests, and receive feedback through an accessible web and mobile interface.

The platform centralizes service workflows for city administrators, improving transparency, responsiveness, and public trust.

Currently, we are in the process of selling the platform in the British Council across the United Kingdom to manage service requests and track the status of issues.`,
      role: "Co-founder & CTO",
      problem: "Citizens often face friction when interacting with municipal services due to fragmented systems, poor communication, and lack of transparency."
    },
    technologies: [
      "Python", "JavaScript",
      "Django", "React", "FastAPI",
      "PostgreSQL", "Redis",
      "GraphQL", "LLM",
      "Docker", "Kubernetes", "AWS", "Prometheus", "Grafana", "ELK Stack"
    ],
    timeline: {
      duration: "18 months (2023 - 2025)",
      status: "Failed"
    },
    features: [
      { title: "Service Request Management", description: "Allows citizens to file service requests (e.g., waste, potholes, streetlights) with automated routing to the relevant departments and live status tracking." },
      { title: "Real-time Analytics", description: "Live dashboards for municipal staff with engagement metrics, issue resolution times, and service backlogs visualized using Grafana and Prometheus." },
      { title: "Mobile Integration", description: "Native iOS and Android apps with offline reporting capabilities and push notifications for status updates." },
      { title: "Social Network", description: "Social network for citizens to connect with each other and share their experiences with the city." },
      { title: "Role-Based Admin Panels", description: "Granular access control for city staff (e.g., department-level views, analyst vs. supervisor roles)." }
    ],
    challenges: [
      {
        title: "High Availability",
        description: "Ensuring near-zero downtime for a platform used by thousands of citizens and city employees.",
        solution: "Implemented Kubernetes multi-region redundancy, automated failover, and real-time health checks with alerting via Prometheus and Grafana."
      },
      {
        title: "Scalable Logging and Monitoring",
        description: "Needed a scalable log and metric pipeline across multiple microservices.",
        solution: "Deployed centralized observability stack using the ELK Stack (Elasticsearch, Logstash, Kibana) for logs and Prometheus/Grafana for metrics."
      }
    ],
    screenshots: [
      { url: "/projects/city_voice/overview.webp", alt: "City Voice Overview Dashboard" },
      { url: "/projects/city_voice/council_dashboard.webp", alt: "Council Dashboard" },
      { url: "/projects/city_voice/reports.webp", alt: "Reports and Analytics" }
    ],
    links: {
      demo: "https://cityvoice.co.uk"
    },
    reflection: {
      learned: "Working on public sector infrastructure reinforced the importance of reliability, accessibility, and user trust. We had to treat every deployment and downtime risk with enterprise-level discipline.",
      differently: "I would start testing failover scenarios and disaster recovery procedures earlier in the infrastructure planning phase, and engage more end-users during feature prototyping."
    }
  },
  {
    id: 'chronolex',
    title: "Chronolex",
    summary: "AI-powered legal document management platform with version control, collaborative editing, and calendar integrations. Sold to a UK law firm for £50,000 and integrated with their existing systems.",
    metrics: [
      { value: "£50k", label: "sold for" },
      { value: "AI", label: "document parsing" },
      { value: "6 mo", label: "build time" },
    ],
    overview: {
      description: `Chronolex is a legal-tech platform designed to help law firms manage case-related documents with accuracy, traceability, and compliance.

It offers a unified system for tracking document versions, assigning deadlines, and ensuring collaboration between legal teams through real-time editing and calendar integrations.

Powered by AI, Chronolex automatically parses documents, extracts metadata, and identifies key deadlines, helping legal professionals stay organized and audit-ready in high-pressure environments.

This project has been sold to a law firm in the UK for 50000 GBP and integrated with their existing system.`,
      role: "Co-founder & Lead Backend Developer",
      problem: "Law firms often work with hundreds of documents per case, and managing versions, tracking changes, and maintaining compliance is tedious and error-prone."
    },
    technologies: [
      "React", "VueJS",
      "Python",
      "FastAPI",
      "PostgreSQL", "MongoDB", "Redis",
      "OpenAI",
      "Docker", "AWS", "Lambda", "Kubernetes"
    ],
    timeline: {
      duration: "6 months (2024)",
      status: "Sold"
    },
    features: [
      { title: "AI-Powered Document Parsing", description: "Uses NLP to extract keywords, suggest tags, and recommend related legal documents for cross-referencing." },
      { title: "Document Version Control", description: "Tracks every edit to the document with full change history and rollback capability." },
      { title: "Google Calendar and Outlook Integrations", description: "Sync document deadlines, case timelines, and review schedules directly with team calendars." },
      { title: "Role-Based Access Control (RBAC)", description: "Supports multiple roles (admin, editor, viewer) with fine-grained access permissions for secure collaboration." },
    ],
    challenges: [
      {
        title: "Real-time Updates Without Race Conditions",
        description: "Implementing collaborative editing in legal documents required strong consistency guarantees.",
        solution: "Used Django Channels with Redis pub/sub and optimistic locking to manage concurrent updates safely."
      }
    ],
    screenshots: [
      { url: "/projects/chronolex/ai_document.jpg", alt: "AI Document Processing" },
      { url: "/projects/chronolex/version_control.jpg", alt: "Document Version Control" },
      { url: "/projects/chronolex/ai_solution.jpg", alt: "AI-Powered Legal Solutions" }
    ],
    links: {
      demo: "https://chronolex.co.uk"
    },
    reflection: {
      learned: "Legal software demands rigorous attention to detail, especially in terms of audit trails, data security, and usability for non-technical users. I gained experience balancing backend performance with UX needs.",
      differently: "Next time, I would prioritize automated testing and accessibility from the beginning, and work more closely with legal professionals to refine workflows earlier in development."
    }
  },
  {
    id: 'skillgrid',
    title: "SkillGrid",
    summary: "AI-powered competency matrix generator that automates the creation of skill assessments for IT roles. Input a role name and get structured subjects, topics, and competency descriptions across multiple skill levels.",
    metrics: [
      { value: "AI", label: "matrix generation" },
      { value: "Drag & Drop", label: "editing" },
      { value: "Excel", label: "export" },
    ],
    overview: {
      description: `SkillGrid is an AI-powered tool that automates the creation of competency matrices for IT roles. Users input a role name (e.g., "Senior React Developer"), and the platform generates relevant subjects, topics, and competency descriptions across multiple skill levels.

The platform features drag-and-drop editing for reorganizing competencies between levels, custom grade level definitions (Junior/Middle/Senior or custom labels), Excel export for sharing matrices as spreadsheets, and structured assessment document generation for reviews and hiring.

Currently in early access with free tier offering all core features.`,
      role: "Founder & Developer",
      problem: "Creating competency matrices for technical roles is tedious and time-consuming, often resulting in inconsistent or incomplete assessments."
    },
    technologies: [
      "TypeScript",
      "Next.js", "React",
      "PostgreSQL",
      "OpenAI",
      "Tailwind CSS",
      "Vercel"
    ],
    timeline: {
      duration: "2025 - Present",
      status: "Active"
    },
    features: [
      { title: "AI-Powered Generation", description: "Automatically creates subjects and topics from role descriptions using AI" },
      { title: "Drag-and-Drop Editing", description: "Modify competencies inline and reorganize between skill levels" },
      { title: "Custom Grade Levels", description: "Define role-specific grading systems with custom labels" },
      { title: "Excel Export", description: "Download completed matrices as spreadsheets" },
      { title: "Assessment Documents", description: "Generate structured evaluation forms for reviews and hiring" },
    ],
    challenges: [
      {
        title: "Consistent AI Output",
        description: "Ensuring AI-generated competency descriptions are consistently structured and relevant across different roles",
        solution: "Designed a multi-step prompt pipeline with validation and formatting constraints to ensure uniform output quality"
      }
    ],
    screenshots: [
      { url: "/projects/skillgrid/landing.webp", alt: "SkillGrid Landing Page" },
      { url: "/projects/skillgrid/ai-generation.webp", alt: "AI-Powered Role & Topic Generation" },
      { url: "/projects/skillgrid/editing.webp", alt: "Competency Matrix Inline Editing" },
      { url: "/projects/skillgrid/grades.webp", alt: "Skill Matrix with Custom Grade Levels" },
    ],
    links: {
      demo: "https://skillgrid.pro"
    },
    reflection: {
      learned: "Building an AI-first product with structured output taught me the importance of prompt engineering and output validation for consistent user experience.",
      differently: "Would invest more in user testing earlier to validate the competency framework structure before building the full generation pipeline."
    }
  },
  {
    id: 'sashas-corner',
    title: "Sasha's Corner",
    summary: "Custom mobile ordering and loyalty platform built for our own specialty coffee shop in the UK. Handles real-time orders, rewards, and payments for actual daily customers via Stripe integration.",
    metrics: [
      { value: "Live", label: "serving customers" },
      { value: "Stripe", label: "payments" },
      { value: "Loyalty", label: "rewards engine" },
    ],
    overview: {
      description: `Sasha's Corner is a mobile-first digital platform developed for my own specialty coffee shop business in the UK.

As both co-founder and technical lead, I designed and implemented a full backend system to support the day-to-day operations of our real-world store - including menu browsing, ordering, loyalty tracking, and user engagement.

Customers can place orders, earn rewards, and manage preferences in real-time. With a focus on flexibility and scalability, the platform continues to serve actual customers every day.`,
      role: "Founder & Backend Developer",
      problem: "Needed a custom, scalable backend to power a real coffee shop's mobile ordering and loyalty platform."
    },
    technologies: [
      "Python",
      "Django",
      "PostgreSQL", "Redis",
      "Docker", "Swagger"
    ],
    timeline: {
      duration: "Ongoing (2024 – Present)",
      status: "Backend Implemented"
    },
    features: [
      { title: "Menu & Item Management", description: "Multiple categories, item variations, and image support — all dynamically managed through the backend." },
      { title: "Customizable Cart & Checkout", description: "Real-time cart system with add-ons, dynamic pricing, and secure order placement logic." },
      { title: "Loyalty & Rewards Engine", description: "Customer retention through point accumulation and redemption." },
      { title: "Payment Integration", description: "Stripe integration for secure and seamless payments." },
      { title: "Favorites & Ratings", description: "Personalized UX allowing users to favorite and rate items, feeding into analytics and menu optimization." },
    ],
    challenges: [
      {
        title: "Evolving Product Requirements",
        description: "Building a product for our real store meant new features and business logic were constantly evolving.",
        solution: "Focused on flexible schema design, config-based behavior, and hot-reloadable menu logic."
      }
    ],
    screenshots: [
      { url: "/projects/sashascorner/item.webp", alt: "Menu Item Management" },
      { url: "/projects/sashascorner/cart.webp", alt: "Shopping Cart Interface" },
      { url: "/projects/sashascorner/rewards.webp", alt: "Loyalty Rewards System" }
    ],
    links: {
      demo: "https://sashascorner.co.uk",
      swagger: "https://sashascorner.co.uk/swagger/"
    },
    reflection: {
      learned: "Owning both the business and the tech stack gave me insight into the challenges of building scalable software with live customer usage. I learned the importance of iteration, data flexibility, and observability.",
      differently: "I would switch to a non-relational database (MongoDB or DynamoDB) due to the highly dynamic nature of product options and user preferences. I'd also implement better observability (logs, metrics, error tracking) from the start."
    }
  },
];

export const PROJECT_CARDS = [
  { id: 'swetrix',        short: 'GDPR-compliant web analytics handling 1M+ req/sec. Open-source Google Analytics alternative.' },
  { id: 'lifegraph',      short: 'AI-powered goal intelligence platform that transforms aspirations into structured, actionable roadmaps.' },
  { id: 'skillgrid',      short: 'AI-powered competency matrix generator for IT roles. Automates skill assessments with drag-and-drop editing.' },
  { id: 'gipant',         short: 'Donation platform connecting consumers with Norwegian sports clubs through bottle recycling.' },
  { id: 'chronolex',      short: 'AI-powered legal document management with version control. Sold for \u00a350,000.' },
  { id: 'cityvoice',      short: 'Smart city platform for citizen-government communication and municipal service management.' },
  { id: 'sashas-corner',  short: 'Mobile ordering and loyalty platform for a real coffee shop business in the UK.' },
];

export const getProjectById = (id) => {
  return projects.find(project => project.id === id);
};
